import { useEffect, useState, useCallback } from "react";
import { useConfirm } from "@/components/ConfirmProvider";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import Pagination, { PAGE_SIZE } from "@/components/Pagination";
import { Plus, Pencil, Trash2 } from "lucide-react";

const empty = { name: "", price: 0, pricing_type: "per_kg", active: true };

export default function Services() {
  const { settings } = useAuth();
  const cur = settings?.currency || "KMF";
  const [list, setList] = useState([]);
  const { confirm } = useConfirm();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [page, setPage] = useState(1);

  const load = useCallback(() => api.get("/services").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const paged = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ name: s.name, price: s.price, pricing_type: s.pricing_type, active: s.active }); setEditId(s.id); setOpen(true); };

  const save = async () => {
    try {
      const body = { ...form, price: Number(form.price) };
      if (editId) await api.put(`/services/${editId}`, body); else await api.post("/services", body);
      toast.success("Service enregistré"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (s) => { try { await api.put(`/services/${s.id}`, { ...s, active: !s.active }); load(); } catch (e) { toast.error(apiError(e)); } };
  const remove = async (id) => { if (!(await confirm({ title: "Supprimer le service", description: "Supprimer ce service ?", confirmText: "Supprimer", danger: true }))) return; try { await api.delete(`/services/${id}`); toast.success("Supprimé"); load(); } catch (e) { toast.error(apiError(e)); } };

  const selCls = "w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800">Services</h1>
        <Button data-testid="add-service-btn" onClick={openNew} className="bg-sky-600 hover:bg-sky-700"><Plus size={18} className="mr-2" /> Nouveau service</Button>
      </div>

      {/* Mobile : cartes (le tableau serait coupé à droite sur petit écran) */}
      <div className="space-y-3 md:hidden">
        {paged.map((s) => (
          <Card key={s.id} data-testid={`service-card-${s.id}`} className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-slate-800 truncate">{s.name}</p>
                <p className="text-sky-700 font-semibold mt-0.5">{fmtMoney(s.price, cur)}{s.pricing_type === "per_kg" ? "/kg" : ""}</p>
                <p className="text-xs text-slate-400 mt-0.5">{s.pricing_type === "per_kg" ? "Au kg" : "Forfait"}</p>
              </div>
              <div className="flex flex-col items-center gap-1 shrink-0">
                <Switch data-testid={`toggle-service-m-${s.id}`} checked={s.active} onCheckedChange={() => toggle(s)} />
                <span className="text-[11px] text-slate-400">{s.active ? "Actif" : "Inactif"}</span>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" data-testid={`edit-service-m-${s.id}`} onClick={() => openEdit(s)} className="flex-1 border-sky-200 text-sky-700 hover:bg-sky-50">
                <Pencil size={15} className="mr-1.5" /> Modifier
              </Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-200" data-testid={`delete-service-m-${s.id}`} onClick={() => remove(s.id)}><Trash2 size={15} /></Button>
            </div>
          </Card>
        ))}
        {list.length === 0 && <p className="text-center text-slate-400 py-6">Aucun service</p>}
      </div>

      {/* Desktop : tableau */}
      <Card className="overflow-hidden hidden md:block">
        <table className="data-table w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left"><tr><th className="px-4 py-3">Nom</th><th className="px-4 py-3 num">Prix</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Actif</th><th className="px-4 py-3 text-right">Actions</th></tr></thead>
          <tbody>
            {paged.map((s) => (
              <tr key={s.id} data-testid={`service-row-${s.id}`} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium text-slate-800">{s.name}</td>
                <td className="px-4 py-3 num">{fmtMoney(s.price, cur)}{s.pricing_type === "per_kg" ? "/kg" : ""}</td>
                <td className="px-4 py-3 text-slate-500">{s.pricing_type === "per_kg" ? "Au kg" : "Forfait"}</td>
                <td className="px-4 py-3"><Switch data-testid={`toggle-service-${s.id}`} checked={s.active} onCheckedChange={() => toggle(s)} /></td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" data-testid={`edit-service-${s.id}`} onClick={() => openEdit(s)} className="mr-1 border-sky-200 text-sky-700 hover:bg-sky-50">
                    <Pencil size={15} className="mr-1.5" /> Modifier
                  </Button>
                  <Button size="icon" variant="ghost" className="text-red-600" data-testid={`delete-service-${s.id}`} onClick={() => remove(s.id)}><Trash2 size={16} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Pagination page={page} total={list.length} onPage={setPage} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Modifier le service" : "Nouveau service"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom</Label><Input data-testid="service-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Prix</Label><Input data-testid="service-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
            <div><Label>Type de tarification</Label>
              <select data-testid="service-pricing-type" className={selCls} value={form.pricing_type} onChange={(e) => setForm({ ...form, pricing_type: e.target.value })}>
                <option value="per_kg">Prix au kg</option>
                <option value="fixed">Prix fixe (forfait)</option>
              </select>
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Actif</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="save-service-btn" onClick={save} className="bg-sky-600 hover:bg-sky-700">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
